import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  fetchLines, fetchJourneys,
  Journey, FilterMode,
  formatTime, statusColour,
} from './analysisApi';

import LineMetricsBox from './LineMetricsBox';
import AccuracyBox from './AccuracyBox';
import { useI18n } from '../i18n/I18nContext';
import { useHomeLive } from '../home/useHomeLive';
import { toAccuracyNode } from './accuracyFromFirestore';


// ---- Colours ----
// text/textMuted/textDim were rgba white at 0.75/0.35/0.20 on #0f2033 — the
// lower two were ~2.5:1 and ~1.7:1, below readable. Explicit composites now,
// matching AccuracyBox and LineMetricsBox.
const C = {
  bg:           '#0f2033',
  bgHover:      '#162840',
  bgSelected:   '#1a3550',
  border:       'rgba(240,165,0,0.15)',
  label:        '#93a3b5',
  text:         '#dbe3ec',
  textMuted:    '#a8b6c4',
  textDim:      '#7d8fa3',
  filterActive: '#f0a500',
  filterBg:     'rgba(240,165,0,0.12)',
  amber:        '#f0a500',
  red:          '#f87171',
  green:        '#4ade80',
};

function berlinToday(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

function formatDayHeading(isoDate: string, locale: string): string {
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString(locale, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function stufeColour(stufe: string | null | undefined): string {
  if (stufe === 'NIEDRIG') return '#4ade80';
  if (stufe === 'MITTEL')  return '#f0a500';
  if (stufe === 'HOCH')    return '#f87171';
  return 'rgba(255,255,255,0.15)';
}

type ActiveFilter = 'live' | 'recent' | 'all' | 'date';

type Props = {
  selectedJourneyId: string | null;
  onSelectJourney:   (journey: Journey) => void;
};

const MessagesSidebar: React.FC<Props> = ({ selectedJourneyId, onSelectJourney }) => {
  const { t, locale } = useI18n();
  const today = berlinToday();

  const [lines,           setLines]           = useState<string[]>([]);
  const [selectedLine,    setSelectedLine]    = useState<string | null>(null);
  const [activeFilter,    setActiveFilter]    = useState<ActiveFilter>('live');
  const [pickedDate,      setPickedDate]      = useState<string>(today);
  const [journeys,        setJourneys]        = useState<Journey[]>([]);
  const [loadingLines,    setLoadingLines]    = useState(true);
  const [loadingJourneys, setLoadingJourneys] = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [hideStale,       setHideStale]       = useState(false);

  const effectiveDay = activeFilter === 'date' ? pickedDate : today;

  // ---------------------------------------------------------------------------
  // Forecast quality, from Firestore rather than the accuracy-service.
  //
  // WHY NOT THE API. REACT_APP_ACCURACY_API_URL is not injected by the build
  // pipeline, so BASE_URL is empty and the fetch goes to the web root; Caddy
  // answers with index.html at status 200 and res.json() dies on "<!DOCTYPE".
  // That is the `Unexpected token '<'` this box was showing.
  //
  // WHAT THIS COVERS. The hourly rollup publishes by_line for exactly two days —
  // today (partial, growing) and the last complete day. The sidebar defaults to
  // today, and the date filter can reach any day, so a picked date outside those
  // two has no Firestore answer. It gets an explicit note rather than falling
  // through to the broken fetch, because "no samples that day" and "this source
  // does not hold that day" are different claims.
  //
  // Arbitrary dates need the API, which needs the pipeline variables — and that
  // ships the key in the bundle, where it would also unlock the service's two
  // POST routes. Deferred until the read and write keys are split.
  // ---------------------------------------------------------------------------
  const accuracy = useHomeLive().doc?.accuracy ?? null;

  const accSource =
    accuracy?.today?.day === effectiveDay ? accuracy.today
    : accuracy?.last_complete_day?.day === effectiveDay ? accuracy.last_complete_day
    : null;

  // `undefined` would make AccuracyBox self-fetch, which is the broken path.
  // An empty node keeps it presentational and lets `note` explain the gap.
  const accNode = selectedLine
    ? (accSource?.by_line?.[selectedLine]
        ? toAccuracyNode(accSource.by_line[selectedLine])
        : {})
    : {};

  // Undefined once the day IS held — an empty node then genuinely means the line
  // had no samples, which is what AccuracyBox's own "Keine Daten" already says.
  const accNote = !accuracy
    ? 'Rollup noch nicht veröffentlicht'
    : !accSource
      ? `Nur ${[accuracy.today?.day, accuracy.last_complete_day?.day]
          .filter(Boolean).join(' und ')} verfügbar`
      : undefined;

  // A partial day must say so — the figure climbs all day as trains reach their
  // target stops, and at 08:00 it rests on a fraction of the samples it will.
  const accPartial = !!accSource && accSource === accuracy?.today;
  const apiFilter: FilterMode =
    activeFilter === 'date' ? 'today' :
    activeFilter === 'all'  ? 'today' :
    activeFilter;

  // ---- Load lines ----
  useEffect(() => {
    setLoadingLines(true);
    fetchLines(effectiveDay)
      .then(r => {
        setLines(r.lines);
        if (r.lines.length > 0) setSelectedLine(r.lines[0]);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoadingLines(false));
  }, [effectiveDay]);

  // ---- Load journeys ----
  useEffect(() => {
    if (!selectedLine) return;
    setLoadingJourneys(true);
    setError(null);
    fetchJourneys({ day: effectiveDay, line: selectedLine, filter: apiFilter })
      .then(r => setJourneys(r.journeys))
      .catch(e => setError(e.message))
      .finally(() => setLoadingJourneys(false));
  }, [selectedLine, activeFilter, pickedDate, effectiveDay, apiFilter]);

  return (
    <Box sx={{
      width: 220, minWidth: 220, height: '100%',
      backgroundColor: C.bg,
      borderRight: `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column',
      fontFamily: '"IBM Plex Sans", sans-serif',
      overflowY: 'hidden',
    }}>

      {/* Day heading */}
      <Box sx={{ px: '12px', pt: '14px', pb: '10px', borderBottom: `1px solid ${C.border}` }}>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10.5px', letterSpacing: '0.1em', color: C.label, textTransform: 'uppercase', mb: '4px' }}>
          {t('an.date')}
        </Typography>
        <Typography sx={{ fontFamily: '"IBM Plex Sans"', fontSize: '12.5px', color: activeFilter === 'date' ? C.filterActive : '#ffffff', fontWeight: 500, lineHeight: 1.3 }}>
          {formatDayHeading(effectiveDay, locale)}
        </Typography>
      </Box>

      {/* Filter tabs */}
      <Box sx={{ p: '10px 12px 8px', borderBottom: `1px solid ${C.border}` }}>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10.5px', letterSpacing: '0.1em', color: C.label, textTransform: 'uppercase', mb: 1 }}>
          {t('an.filter')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
          {(['live', 'recent', 'all'] as ActiveFilter[]).map(f => (
            <Box key={f} onClick={() => setActiveFilter(f)} sx={{
              flex: 1, textAlign: 'center', py: '4px', px: '6px', borderRadius: '3px',
              cursor: 'pointer', fontSize: '11px', fontFamily: '"IBM Plex Sans"',
              color:           activeFilter === f ? C.filterActive : C.textMuted,
              backgroundColor: activeFilter === f ? C.filterBg     : 'transparent',
              border:          activeFilter === f ? '1px solid rgba(240,165,0,0.4)' : '1px solid transparent',
              transition: 'all 0.15s ease',
              '&:hover': { color: C.filterActive },
            }}>
              {f === 'live' ? t('filter.live') : f === 'recent' ? t('filter.recent') : t('filter.all')}
            </Box>
          ))}
        </Box>

        {/* Date picker */}
        <Box onClick={() => setActiveFilter('date')} sx={{ cursor: 'pointer' }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10.5px', letterSpacing: '0.1em', color: C.label, textTransform: 'uppercase', mb: '4px' }}>
            {t('an.pickDate')}
          </Typography>
          <input
            type="date"
            value={pickedDate}
            max={today}
            onChange={e => { setPickedDate(e.target.value); setActiveFilter('date'); }}
            style={{
              width: '100%',
              backgroundColor: activeFilter === 'date' ? 'rgba(240,165,0,0.08)' : 'rgba(255,255,255,0.04)',
              border: activeFilter === 'date' ? '1px solid rgba(240,165,0,0.4)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '3px',
              color: activeFilter === 'date' ? '#f0a500' : '#a8b6c4',
              padding: '4px 6px', fontSize: '12px',
              fontFamily: '"IBM Plex Mono", monospace',
              outline: 'none', boxSizing: 'border-box', colorScheme: 'dark',
            }}
          />
        </Box>
      </Box>

      {/* Line selector */}
      <Box sx={{ p: '10px 12px 8px', borderBottom: `1px solid ${C.border}` }}>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10.5px', letterSpacing: '0.1em', color: C.label, textTransform: 'uppercase', mb: 1 }}>
          {t('an.line')}
        </Typography>
        {loadingLines ? (
          <CircularProgress size={16} sx={{ color: C.filterActive }} />
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {lines.map(line => (
              <Box key={line} onClick={() => setSelectedLine(line)} sx={{
                px: '8px', py: '3px', borderRadius: '3px', cursor: 'pointer',
                fontSize: '12px', fontFamily: '"IBM Plex Mono"', fontWeight: 500,
                color:           selectedLine === line ? '#0d1b2a' : C.textMuted,
                backgroundColor: selectedLine === line ? C.filterActive : 'transparent',
                border:          selectedLine === line ? `1px solid ${C.filterActive}` : '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.15s ease',
                '&:hover': { color: selectedLine === line ? '#0d1b2a' : C.filterActive, borderColor: C.filterActive },
              }}>
                {line}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <LineMetricsBox line={selectedLine} day={effectiveDay} />
      {selectedLine && (
        <Box sx={{ px: '12px', py: '10px', borderBottom: `1px solid ${C.border}` }}>
          <AccuracyBox
            scope="line"
            line={selectedLine}
            day={effectiveDay}
            node={accNode}
            note={accNote}
            title={accPartial
              ? `${t('acc.title')} — ${t('acc.partialDay')}`
              : undefined}
          />
        </Box>
      )}


      {/* Journey list */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: '8px 0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '12px', pb: '6px' }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10.5px', letterSpacing: '0.1em', color: C.label, textTransform: 'uppercase' }}>
            {t('an.trains')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {!loadingJourneys && (
              <Typography sx={{ fontSize: '10px', color: C.textMuted, fontFamily: '"IBM Plex Mono"' }}>
                {(hideStale ? journeys.filter(j => !j.stale) : journeys).length}
              </Typography>
            )}
            {/* Hide stale toggle */}
            <Box
              onClick={() => setHideStale(h => !h)}
              sx={{
                px: '5px', py: '2px', borderRadius: '3px', cursor: 'pointer',
                fontSize: '9px', fontFamily: '"IBM Plex Mono"', letterSpacing: '0.06em',
                color:           hideStale ? '#0d1b2a' : C.red,
                backgroundColor: hideStale ? C.red     : 'rgba(248,113,113,0.1)',
                border:          hideStale ? `1px solid ${C.red}` : '1px solid rgba(248,113,113,0.35)',
                transition: 'all 0.15s ease',
                userSelect: 'none',
                '&:hover': { borderColor: C.red, backgroundColor: hideStale ? '#e05555' : 'rgba(248,113,113,0.2)' },
              }}
            >
              {hideStale ? t('an.staleHide') : t('an.staleShow')}
            </Box>
          </Box>
        </Box>

        {loadingJourneys && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <CircularProgress size={20} sx={{ color: C.filterActive }} />
          </Box>
        )}

        {error && (
          <Typography sx={{ fontSize: '11px', color: C.red, px: 2, pt: 1 }}>{error}</Typography>
        )}

        {!loadingJourneys && !error && (hideStale ? journeys.filter(j => !j.stale) : journeys).map(journey => {
          const isSelected = journey.journey_id === selectedJourneyId;
          const sColor     = statusColour(journey.status);

          return (
            <Box
              key={journey.journey_id}
              onClick={() => onSelectJourney(journey)}
              sx={{
                px: '12px', py: '8px', cursor: 'pointer',
                backgroundColor: isSelected ? C.bgSelected : 'transparent',
                borderLeft: isSelected ? `2px solid ${C.amber}` : '2px solid transparent',
                transition: 'background-color 0.1s ease',
                '&:hover': { backgroundColor: isSelected ? C.bgSelected : C.bgHover },
              }}
            >
              {/* Route ID + departure time */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '3px' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', minWidth: 0, overflow: 'hidden' }}>
                  <Typography sx={{
                    fontSize: '13px', fontWeight: 600, fontFamily: '"IBM Plex Mono"',
                    color: isSelected ? '#ffffff' : C.text, flexShrink: 0,
                  }}>
                    {journey.route_id}
                  </Typography>
                  {journey.destination_stop_name && (
                    <Typography sx={{
                      fontSize: '10px', fontWeight: 600, fontFamily: '"IBM Plex Sans"',
                      color: 'rgba(255,255,255,0.85)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {journey.destination_stop_name}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: '11px', color: C.textMuted, fontFamily: '"IBM Plex Mono"', flexShrink: 0, ml: '4px' }}>
                  {formatTime(journey.scheduled_departure)}
                </Typography>
              </Box>

              {/* Status + stale flag */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: '3px' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: sColor, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '10.5px', color: sColor, fontFamily: '"IBM Plex Mono"', letterSpacing: '0.04em' }}>
                    {journey.status}
                </Typography>
                {journey.stale && (
                  <Box sx={{
                    ml: 'auto',
                    px: '4px', py: '1px', borderRadius: '3px',
                    backgroundColor: 'rgba(248,113,113,0.15)',
                    border: '1px solid rgba(248,113,113,0.4)',
                  }}>
                    <Typography sx={{ fontSize: '9px', fontFamily: '"IBM Plex Mono"', color: C.red, letterSpacing: '0.08em' }}>
                      STALE
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Latest stop */}
              {journey.latest_stop_name && (
                <Typography sx={{
                  fontSize: '10px', color: 'rgba(255,255,255,0.85)',
                  fontFamily: '"IBM Plex Sans"', fontWeight: 700, fontStyle: 'italic',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}>
                  ↳ {journey.latest_stop_name}
                </Typography>
              )}
              {/* VU occupancy indicator */}
              {journey.vu_ist_belegung != null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '4px' }}>
                  {[...Array(4)].map((_, i) => (
                    <Box key={i} sx={{
                      width: 10, height: 6, borderRadius: '1px',
                      backgroundColor: stufeColour(journey.vu_ist_auslastungsstufe),
                      opacity: 0.85,
                    }} />
                  ))}
                  <Typography sx={{
                    fontFamily: '"IBM Plex Mono"',
                    fontSize: '10px',
                    color: stufeColour(journey.vu_ist_auslastungsstufe),
                    ml: '2px',
                  }}>
                    {journey.vu_ist_belegung}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}

        {!loadingJourneys && !error && (hideStale ? journeys.filter(j => !j.stale) : journeys).length === 0 && (
          <Typography sx={{ fontSize: '12px', color: C.textMuted, px: 2, pt: 2, fontFamily: '"IBM Plex Sans"' }}>
            {t('an.noTrains')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MessagesSidebar;
